import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { safeText } from '../lib/render';

export default function WorldBankTask() {
  const { taskId } = useParams();
  const nav = useNavigate();
  const [status, setStatus] = useState('queued');
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState(null);
  const stopped = useRef(false);

  useEffect(() => {
    if (!taskId) return;
    stopped.current = false;
    let timer = null;

    async function poll() {
      if (stopped.current) return;
      try {
        const res = await fetch(`/api/worldbank/task/${taskId}`, {
          headers: await authHeader(),
        });

        if (res.status === 202) {
          const body = await res.json();
          setStatus(safeText(body.status, 'running'));
          // body.progress can be either a string or an object like
          // {step, step_key, detail} coming from _record_progress in app.py.
          // safeText picks the most human field, so React never tries to
          // render an object directly (React error #31).
          setProgress(safeText(body.progress, ''));
        } else if (res.ok) {
          const body = await res.json();
          stopped.current = true;
          if (body.analysis_id) {
            nav(`/worldbank/${body.analysis_id}`);
          } else {
            setStatus('complete');
          }
          return;
        } else {
          const body = await res.json().catch(() => ({}));
          setErr(body.error || `HTTP ${res.status}`);
          stopped.current = true;
          return;
        }
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Poll failed');
      }
      timer = setTimeout(poll, 3000);
    }

    void poll();
    return () => {
      stopped.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [taskId, nav]);

  return (
    <div className="main-container">
      <div className="row">
        <div className="col-lg-6 mx-auto">
          <div className="card mt-4">
            <div className="card-body text-center p-5">
              <div
                className="mx-auto mb-3"
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: 16,
                  background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 30,
                  boxShadow: '0 8px 24px rgba(79,70,229,0.25)',
                }}
              >
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="mb-2">Running analysis</h3>
              <div className="text-muted small mb-3 text-capitalize">{safeText(status)}</div>
              {progress && (
                <div className="text-muted small mb-3">
                  <i className="fas fa-cog fa-spin me-2"></i>
                  {safeText(progress)}
                </div>
              )}
              <div className="progress" style={{ height: 6 }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{
                    width: '60%',
                    background: 'linear-gradient(90deg, var(--primary), var(--secondary))',
                  }}
                />
              </div>

              {err && <div className="alert alert-danger mt-4 mb-0">{err}</div>}

              <Link to="/worldbank" className="btn btn-outline-secondary mt-4">
                <i className="fas fa-arrow-left me-1"></i>Back to list
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

async function authHeader() {
  const { auth } = await import('../lib/firebase');
  const u = auth.currentUser;
  if (!u) return {};
  return { Authorization: `Bearer ${await u.getIdToken()}` };
}
